import { useState } from 'react';
import { Typography, Card, CardContent, styled, Button } from '@mui/material';
import { Column, Row } from 'further-ui/components/Layout';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Delete, Edit, RemoveRedEye } from '@mui/icons-material';
import PageFilters, { Filter } from 'components/new/PageFilters';
import SearchField from 'further-ui/components/SearchField';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { FC } from 'react';
import { useLayoutContext } from 'contexts/LayoutContext';
import useListDocuments from './useListDocuments';
import AddDocument from '../AddDocument';
import EditSignatureLocations from '../EditSignatureLocations';
import ViewEditDocumentName from './ViewEditDocumentName';
import DocumentEditingStatus from './DocumentEditingStatus';
import api from 'lib/trpcClient';
import { EditingStatus } from 'further-types/lp/vehicle-signing-document';

const pageId = 'onboarding-document-signing';

const DocumentLibraryContainer = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.sm),
}));

const DocumentSigning: FC = () => {
  const { selectedVehicleId } = useLayoutContext();
  const [documentIdBeingRenamed, setDocumentIdBeingRenamed] = useState<
    string | null
  >(null);
  const apiUtils = api.useUtils();

  const {
    filters,
    handleChangeFilters,
    documents,
    isFetching,
    handleRemoveSigningDocument,
    isRemovingSigningDocument,
    deleteDocumentDisclosure,
    addDocumentDisclosure,
    editSignatureLocationsDisclosure,
    runPostEditTemplateChecks,
  } = useListDocuments(pageId, selectedVehicleId);

  const columns: Array<TableColumn<(typeof documents)[number]>> = [
    {
      label: 'Document name',
      key: 'documentName',
      render: (document) => (
        <ViewEditDocumentName
          documentId={document.id}
          documentName={document.documentName}
          isBeingRenamed={documentIdBeingRenamed === document.id}
          onCompletion={() => {
            setDocumentIdBeingRenamed(null);
          }}
        />
      ),
    },
    {
      label: 'Status',
      key: 'editingStatus',
      render: (document) => (
        <DocumentEditingStatus editingStatus={document.editingStatus} />
      ),
    },
    {
      label: 'Uploaded at',
      key: 'createdAt',
      render: (document) => document.createdAt.toLabel(),
    },
  ];

  return (
    <Column spacing="md">
      <Column spacing="xs">
        <Typography variant="h5">Document Signing</Typography>
        <Typography variant="body2">
          Add a new document for signing using the add button below. Assign a
          document signing in each investor's onboarding dashboard tab.
        </Typography>
      </Column>

      <Card variant="outlined">
        <DocumentLibraryContainer>
          <Row fullWidth spacing="sm" justify="space-between" centered>
            <Typography variant="h6">Document library</Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={addDocumentDisclosure.onOpen}
            >
              Add new document
            </Button>
          </Row>
          <PageFilters justify="space-between">
            <Filter size="md">
              <SearchField
                fullWidth
                placeholder="Search by document name"
                value={filters.search}
                onSearch={(query) => {
                  handleChangeFilters({ search: query });
                }}
                autoSearchOnDebounce
                debounceTime={1}
                allowEmptyQuery
              />
            </Filter>
          </PageFilters>
          <Table
            columns={columns}
            rows={documents}
            isLoading={isFetching}
            rowsTotalCount={0}
            disableSorting
            emptyMessage="No documents added"
            actions={[
              {
                label: 'Delete',
                icon: Delete,
                disabled: (document) =>
                  document.editingStatus === EditingStatus.InUse,
                tooltip: (document) =>
                  document.editingStatus === EditingStatus.InUse
                    ? 'Cannot delete document as it is in use.'
                    : '',
                onClick: (document) => {
                  deleteDocumentDisclosure.stageAction({ id: document.id });
                },
              },
              {
                label: 'Preview',
                icon: RemoveRedEye,
                onClick: (document) => {
                  window.open(document.documentPreviewUrl, '_blank');
                },
              },
              {
                label: 'Rename document',
                icon: Edit,
                disabled: (document) =>
                  document.editingStatus === EditingStatus.InUse,
                tooltip: (document) =>
                  document.editingStatus === EditingStatus.InUse
                    ? 'Cannot rename document as it is in use.'
                    : '',
                onClick: (document) => {
                  setDocumentIdBeingRenamed(document.id);
                },
              },
              {
                label: 'Change signature locations',
                icon: Edit,
                disabled: (document) =>
                  document.editingStatus === EditingStatus.InUse,
                tooltip: (document) =>
                  document.editingStatus === EditingStatus.InUse
                    ? 'Cannot change signature locations as this document is in use.'
                    : '',
                onClick: (document) => {
                  editSignatureLocationsDisclosure.stageAction({
                    id: document.id,
                  });
                },
              },
            ]}
          />
        </DocumentLibraryContainer>
      </Card>
      {addDocumentDisclosure.isOpen ? (
        <AddDocument
          open={true}
          onClose={addDocumentDisclosure.onClose}
          onDocumentAdded={(vehicleSigningDocumentId) => {
            editSignatureLocationsDisclosure.stageAction({
              id: vehicleSigningDocumentId,
            });
          }}
        />
      ) : null}
      {editSignatureLocationsDisclosure.isOpen &&
      editSignatureLocationsDisclosure.actionProps.id ? (
        <EditSignatureLocations
          open={true}
          onClose={async () => {
            // run this in case a document was added, but the user didn't trigger the post template edit checks mutation which clears the cache
            editSignatureLocationsDisclosure.onClose();
            await apiUtils.invalidate();
          }}
          vehicleSigningDocumentId={
            editSignatureLocationsDisclosure.actionProps.id
          }
          runPostEditTemplateChecks={() => {
            runPostEditTemplateChecks({
              vehicleSigningDocumentId:
                editSignatureLocationsDisclosure.actionProps.id,
              vehicleId: selectedVehicleId ?? '',
            });
          }}
        />
      ) : null}
      <ConfirmDialog
        open={deleteDocumentDisclosure.isOpen}
        onClose={deleteDocumentDisclosure.onClose}
        title="Remove document"
        content={
          <Typography variant="body2">
            This will remove the document from all investors that have it
            assigned, but have not yet completed it.
          </Typography>
        }
        loading={isRemovingSigningDocument}
        actions={{
          confirm: {
            label: 'Remove',
            color: 'error',
            onClick: () => {
              handleRemoveSigningDocument();
            },
          },
        }}
      />
    </Column>
  );
};

export default DocumentSigning;
